import {Component, inject, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {BehaviorSubject, debounceTime, Observable, of, Subject, Subscription, switchMap, tap} from "rxjs";
import {InvoiceDto} from "../../../repositories/dto/document/invoice.dto";
import {isDefined, isFalse, isTrue, isUuid, nonBlank, unsubscribe} from "../../utils/epic.utils";
import {ModalComponent} from "../../modal/modal.component";
import {ModalConfig} from "../../modal/modal.config";
import {TranslatePipe} from "@ngx-translate/core";
import {CardComponent} from "../../card/card.component";
import {CardBodyComponent} from "../../card/card-body/card-body.component";
import {ButtonComponent} from "../../button/button.component";
import {CorrespondenceService} from "../../../services/correspondence.service";
import {LetterPriceDto} from "../../../repositories/dto/letter-price.dto";
import {IconComponent} from "../../icon/icon.component";
import {SettingsService} from "../../../services/settings.service";
import {SettingKey} from "../../../base/setting-key.enum";
import {CurrencyService} from "../../../services/currency.service";
import {I18nService} from "../../../services/i18n/i18n.service";
import {filter} from "rxjs/operators";
import {Permissions} from "../../../services/auth/permissions.item";
import {PermissionService} from "../../../services/auth/permission.service";
import {HttpErrorResponse} from "@angular/common/http";
import {WrapperDto} from "../../../repositories/dto/wrapper.dto";
import {ExceptionErrorDto} from "../../../repositories/dto/exception-error.dto";

import {UrlService} from "../../../services/url.service";
import {Paths} from "../../../paths";
import {NgbTooltip} from "@ng-bootstrap/ng-bootstrap";
import {SubInvoiceDto} from "../../../repositories/dto/sub-invoice.dto";
import {LoggingService} from "../../../services/logger/logging.service";
import {MediaFileType} from "../../enum/media-file-type.enum";
import {ContactService} from "../../../services/contact.service";
import {Additionals} from "../../../repositories/item/additionals";
import {SendLetterDto} from "../../../repositories/dto/send-letter.dto";
import {ViewerAction} from "../../enum/viewer-action.enum";

@Component({
  selector: 'acc-letter-send-viewer',
  imports: [
    ModalComponent,
    TranslatePipe,
    CardComponent,
    CardBodyComponent,
    ButtonComponent,
    IconComponent,
    NgbTooltip
  ],
  templateUrl: './letter-send-viewer.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './letter-send-viewer.component.css'
})
export class LetterSendViewerComponent implements OnInit, OnDestroy {

  @ViewChild('letterTpl')
  protected letterTpl: ModalComponent | undefined;

  protected readonly config = new ModalConfig({
    inputConfirmMsgCode: 'letter.confirm',
    inputConfirmLoadingTimeout: 10000,
    autoClose: false
  });
  // Settings
  protected printSpectrum: 'COLOR' | 'GRAYSCALE';
  protected printMode: 'SIMPLEX' | 'DUPLEX';
  protected deliveryProduct: 'FAST' | 'CHEAP';
  protected lastCalc = new BehaviorSubject<LetterPriceDto | undefined>(undefined);
  protected errorMsgKey: string | undefined;
  protected perms: Permissions;
  protected recipientLetterAddress: string | undefined;

  private readonly correspondenceService = inject(CorrespondenceService);
  private readonly settingsService = inject(SettingsService);
  private readonly currencyService = inject(CurrencyService);
  private readonly i18n = inject(I18nService);
  private readonly log = inject(LoggingService);
  private readonly permissionService = inject(PermissionService);
  private readonly urlService = inject(UrlService);
  private readonly contactService = inject(ContactService);

  private readonly subs: Subscription[] = [];
  private invoice: InvoiceDto | undefined;
  private subInvoice: SubInvoiceDto | undefined;
  private mediaId: string | undefined;
  private calculate = new Subject<boolean>();
  private priceUnknownMsg: string = '';
  private mediaType: MediaFileType | undefined;
  private contactId: string | undefined;
  private addressId: string | undefined;
  private vehicleId: string | undefined;
  private viewerAction = new BehaviorSubject<ViewerAction>(ViewerAction.NONE);

  constructor() {
    this.perms = this.permissionService.getPerms();
    // set default values
    const def = new LetterPriceDto();
    this.printSpectrum = def.printSpectrum;
    this.printMode = def.printMode;
    this.deliveryProduct = def.deliveryProduct;
    this.i18n.translateAsync('letter.action.price.unknown').then((msg) => this.priceUnknownMsg = msg);
  }

  ngOnInit(): void {
    this.subs.push(
      this.permissionService.accChanged().subscribe((p) => this.perms = p),
      this.calculate.pipe(
        tap(() => {
          this.lastCalc.next(undefined);
          this.confirmButtonSend();
          this.disableConfirmButton(true);
        }),
        debounceTime(1000),
        switchMap(() => {
          this.errorMsgKey = undefined;
          const settings = this.buildSettings();
          return this.calcPrice(settings);
        }))
        .subscribe({
          next: (r) => this.lastCalc.next(r),
          error: (err: HttpErrorResponse) => this.errorMsgKey = new WrapperDto<ExceptionErrorDto>(err.error).getFirstError().message
        }),
      this.settingsService.getSettingOnChange(SettingKey.USER_LETTER_CALC)
        .pipe(filter((s) => nonBlank(s.value)))
        .subscribe((s) => {
          const setting: LetterPriceDto = JSON.parse(s.value!);
          this.printSpectrum = isDefined(setting.printSpectrum) ? setting.printSpectrum : this.printSpectrum;
          this.printMode = isDefined(setting.printMode) ? setting.printMode : this.printMode;
          this.deliveryProduct = isDefined(setting.deliveryProduct) ? setting.deliveryProduct : this.deliveryProduct;
        }),
      this.lastCalc.asObservable().pipe(filter((r) => isDefined(r))).subscribe((r) => {
        if (isTrue(r.sufficientCredit)) {
          this.confirmButtonSend();
        } else {
          this.confirmButtonInsufficientBalance();
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.close();
    unsubscribe(this.subs);
  }

  public getAction(): Observable<ViewerAction> {
    return this.viewerAction.asObservable();
  }

  public openInvoice(invoice: InvoiceDto): Promise<void> {
    this.invoice = invoice;
    this.mediaId = this.invoice.mediaId;
    return this.open();
  }

  public openSubInvoice(subInvoice: SubInvoiceDto): Promise<void> {
    this.subInvoice = subInvoice;
    this.mediaId = this.subInvoice.mediaId;
    return this.open();
  }

  public openVehicle(type: MediaFileType, contactId: string, addressId?: string, vehicleId?: string): Promise<void> {
    this.mediaType = type;
    this.contactId = contactId;
    this.addressId = addressId;
    this.vehicleId = vehicleId;
    return this.open();
  }

  public close(): void {
    this.lastCalc.next(undefined);
    this.viewerAction.next(ViewerAction.CLOSE);
    this.mediaType = undefined;
    this.contactId = undefined;
    this.addressId = undefined;
    this.vehicleId = undefined;
    this.letterTpl?.close();
  }

  protected isPrintSpectrumColor(): boolean {
    return this.printSpectrum === 'COLOR';
  }

  protected setPrintSpectrum(printSpectrum: 'COLOR' | 'GRAYSCALE'): void {
    if (this.printSpectrum !== printSpectrum) {
      this.printSpectrum = printSpectrum;
      this.calculate.next(true);
    }
  }

  protected isPrintModeSimplex(): boolean {
    return this.printMode === 'SIMPLEX';
  }

  protected setPrintMode(printMode: 'SIMPLEX' | 'DUPLEX'): void {
    if (this.printMode !== printMode) {
      this.printMode = printMode;
      this.calculate.next(true);
    }
  }

  protected isDeliveryProductFast(): boolean {
    return this.deliveryProduct === 'FAST';
  }

  protected setDeliveryProduct(deliveryProduct: 'FAST' | 'CHEAP'): void {
    if (this.deliveryProduct !== deliveryProduct) {
      this.deliveryProduct = deliveryProduct;
      this.calculate.next(true);
    }
  }

  protected getPrice(): string {
    if (this.hasPrice()) {
      const amount = this.currencyService.formatAmount(this.lastCalc.value!.price, this.lastCalc.value!.currencyCode!, 2);
      if (isDefined(amount)) {
        //this.disableConfirmButton(false);
        return amount;
      }
    }
    return this.priceUnknownMsg;
  }

  protected orderLetter(): void {
    if (this.perms.correspondence.write && this.hasPrice()) {
      if (isFalse(this.lastCalc.value!.sufficientCredit)) {
        this.urlService.navigate([Paths.ORG_WALLET_TRANSACTION_LIST]).then(() => this.close());
      } else {
        setTimeout(() => this.disableConfirmButton(true), 2000);
        this.disableCancelButton(true);
        const settings = this.buildSettings();
        // First check for the delivery note, as the invoice can be set together with the delivery note
        if (this.isSubInvoice() && isDefined(this.subInvoice)) {
          this.viewerAction.next(ViewerAction.SEND);
          this.subs.push(this.correspondenceService.sendSubInvoiceLetter(this.subInvoice, this.buildSettings()).subscribe(() => {
            this.viewerAction.next(ViewerAction.SENT);
            this.closeAndUpdateSettings(settings);
          }));
        } else if (this.isInvoice() && isDefined(this.invoice)) {
          this.viewerAction.next(ViewerAction.SEND);
          this.subs.push(this.correspondenceService.sendInvoiceLetter(this.invoice.id, this.buildSettings()).subscribe(() => {
            this.viewerAction.next(ViewerAction.SENT);
            this.closeAndUpdateSettings(settings);
          }));
        } else if (isDefined(this.mediaType) && isUuid(this.contactId) && isUuid(this.addressId)) {
          this.viewerAction.next(ViewerAction.SEND);
          this.subs.push(this.correspondenceService.sendLetter(this.buildSendLetterDto()).subscribe({
            next: () => {
              this.viewerAction.next(ViewerAction.SENT);
              this.closeAndUpdateSettings(settings);
            },
            error: (err: HttpErrorResponse) => {
              this.disableCancelButton(false);
              this.disableConfirmButton(true);
              this.errorMsgKey = new WrapperDto<ExceptionErrorDto>(err.error).getFirstError().message;
            }
          }));
        } else {
          throw new Error('Not implemented');
        }
      }
    }
  }

  private getRecipient(): Promise<string | undefined> {
    return new Promise<string | undefined>((resolve) => {
      if (this.isInvoice()) {
        resolve(this.invoice!.contact.letterAddress);
      } else if (this.isSubInvoice()) {
        resolve(this.subInvoice!.contact!.letterAddress);
      } else if (isUuid(this.contactId) && isUuid(this.addressId)) {
        this.subs.push(this.contactService.getById(this.contactId, new Additionals({addAddresses: true}))
          .subscribe((c) => resolve(c.addresses.find(a => a.id === this.addressId)?.letterAddress)));
      } else {
        this.log.error('Recipient for letter "{}": not implemented', [this.mediaType]);
        resolve(undefined);
      }
    });
  }

  private open(): Promise<void> {
    this.lastCalc.next(undefined);
    return new Promise((resolve) => {
      this.letterTpl?.open().then(() => {
        this.disableConfirmButton(true);
        this.viewerAction.next(ViewerAction.OPEN);
        resolve();
      });
      // Perform a calculation when initiating/open
      if (!this.hasPrice()) {
        this.calculate.next(true);
      } else {
        // Workaround: when the popup is closed and opened again
        setTimeout(() => this.lastCalc.next(this.lastCalc.value), 10);
      }
      this.getRecipient().then((a) => this.recipientLetterAddress = a);
    });
  }

  private closeAndUpdateSettings(settings: LetterPriceDto): void {
    this.settingsService.updateUserSetting(SettingKey.USER_LETTER_CALC, JSON.stringify(settings)).then();
    this.close();
  }

  private hasPrice(): boolean {
    return isDefined(this.lastCalc) && isDefined(this.lastCalc.value) && isDefined(this.lastCalc.value.currencyCode)
      && isDefined(this.lastCalc.value.price) && this.lastCalc.value.price > 0;
  }

  private buildSendLetterDto(): SendLetterDto {
    return new SendLetterDto({
      type: this.mediaType,
      contactId: this.contactId,
      addressId: this.addressId,
      vehicleId: this.vehicleId,
      printSpectrum: this.printSpectrum,
      printMode: this.printMode,
      deliveryProduct: this.deliveryProduct
    });
  }

  private buildSettings(): LetterPriceDto {
    return new LetterPriceDto({
      printSpectrum: this.printSpectrum,
      printMode: this.printMode,
      deliveryProduct: this.deliveryProduct
    });
  }

  protected hasError(): boolean {
    return isDefined(this.errorMsgKey);
  }

  private isInvoice(): boolean {
    return isDefined(this.invoice) && isUuid(this.mediaId)
      && isDefined(this.invoice.contact) && isTrue(this.invoice.contact.completeAsLetter);
  }

  private isSubInvoice(): boolean {
    return isDefined(this.subInvoice) && isUuid(this.mediaId)
      && isDefined(this.subInvoice.contact) && isTrue(this.subInvoice.contact.completeAsLetter);
  }

  private calcPrice(settings: LetterPriceDto): Observable<LetterPriceDto> {
    // First check for the delivery note, as the invoice can be set together with the delivery note
    if (this.isSubInvoice() && isDefined(this.subInvoice)) {
      return this.correspondenceService.getSubInvoicePrice(this.subInvoice, settings);
    } else if (this.isInvoice() && isDefined(this.invoice)) {
      return this.correspondenceService.getInvoicePrice(this.invoice.id, settings);
    } else if (isDefined(this.mediaType) && isUuid(this.contactId) && isUuid(this.addressId)) {
      return this.correspondenceService.getLetterPrice(this.contactId, this.addressId, this.mediaType, settings);
    }
    this.log.error('Calc price for letter "{}": not implemented', [this.mediaType]);
    return of();
  }

  private confirmButtonSend(): void {
    this.config.inputConfirmMsgCode = 'letter.confirm';
    this.disableConfirmButton(!this.hasPrice());
    const confirmBtn = this.letterTpl?.getInputConfirmButton();
    if (isDefined(confirmBtn)) {
      confirmBtn.status = 'primary';
    }
  }

  private confirmButtonInsufficientBalance(): void {
    this.config.inputConfirmMsgCode = 'org.wallet.insufficientBalance';
    this.disableConfirmButton(!this.perms.orgWallet.read);
    const confirmBtn = this.letterTpl?.getInputConfirmButton();
    if (isDefined(confirmBtn)) {
      confirmBtn.status = 'danger';
    }
  }

  private disableConfirmButton(disabled: boolean): void {
    const button = this.letterTpl?.getInputConfirmButton();
    if (isDefined(button)) {
      button.disabled = disabled && this.perms.correspondence.write;
    }
  }

  private disableCancelButton(disabled: boolean): void {
    const button = this.letterTpl?.getInputCancelButton();
    if (isDefined(button)) {
      button.disabled = disabled;
    }
  }
}
